import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Portal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DASHBOARD_TUTORIAL_EVENT,
  advanceDashboardTutorial,
  dashboardTutorialBlockRoute,
  engageDashboardTutorialTarget,
  loadDashboardTutorialState,
  loadDashboardTutorialOnEntry,
  markDashboardTutorialBlocked,
  pauseDashboardTutorial,
  refreshDashboardTutorialPlan,
  reportDashboardTutorialMetric,
  tutorialBlockByKey,
} from "../../utils/dashboardTutorial";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import {
  PROJECT_BUSINESS_GOALS,
  PROJECT_BUSINESS_PROFILES,
  businessGoalsForProfile,
  normalizeProjectBusinessProfile,
  projectBusinessGoals,
} from "../../utils/projectCapabilities";

// WebProfit Guided Onboarding · BLOCK 9 item focus coach
// STATUS: ITEM_FOCUS_COACH_IMPLEMENTED · STATIC_PASS · STAGING_PENDING.
// SAFETY: no business action depends on this component. If a DOM target disappears,
// the engine degrades to a safe navigation card instead of trapping the user.
// UX: while locating a target the rest of the page is blocked. Once the user clicks
// the correct action, the page is released so real dialogs/forms remain fully usable.

const OVERLAY_Z = 2147482000;
const TARGET_TIMEOUT_MS = 2400;
const TARGET_POLL_MS = 120;
const TARGET_PADDING = 8;
const BRAND = { orange: "#F35A3A", dark: "#15171C" };

function projectKey(project) {
  return String(project?.slug || project?.id || project?._id || "").trim();
}

function visibleElement(selector) {
  if (!selector || typeof document === "undefined") return null;
  try {
    const nodes = Array.from(document.querySelectorAll(selector));
    return nodes.find((node) => {
      if (!(node instanceof HTMLElement)) return false;
      const rect = node.getBoundingClientRect();
      const style = window.getComputedStyle(node);
      return rect.width > 2 && rect.height > 2 && style.display !== "none" && style.visibility !== "hidden";
    }) || null;
  } catch (_) {
    return null;
  }
}

function normalizedPath(value) {
  const raw = String(value || "").split("?")[0].replace(/\/+$/, "");
  return raw || "/";
}

function blockerStyle(extra = {}) {
  return {
    position: "fixed",
    zIndex: OVERLAY_Z,
    bgcolor: "rgba(7,9,13,.76)",
    pointerEvents: "auto",
    backdropFilter: "blur(1.5px)",
    ...extra,
  };
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function SpotlightBlockers({ rect }) {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const left = clamp(rect.left - TARGET_PADDING, 0, width);
  const top = clamp(rect.top - TARGET_PADDING, 0, height);
  const right = clamp(rect.right + TARGET_PADDING, 0, width);
  const bottom = clamp(rect.bottom + TARGET_PADDING, 0, height);
  return (
    <>
      <Box sx={blockerStyle({ left: 0, top: 0, width: "100vw", height: top })} />
      <Box sx={blockerStyle({ left: 0, top, width: left, height: Math.max(0, bottom - top) })} />
      <Box sx={blockerStyle({ left: right, top, width: Math.max(0, width - right), height: Math.max(0, bottom - top) })} />
      <Box sx={blockerStyle({ left: 0, top: bottom, width: "100vw", height: Math.max(0, height - bottom) })} />
      <Box
        aria-hidden="true"
        sx={{
          position: "fixed",
          zIndex: OVERLAY_Z + 1,
          pointerEvents: "none",
          left,
          top,
          width: Math.max(1, right - left),
          height: Math.max(1, bottom - top),
          borderRadius: 2.5,
          border: "2px solid rgba(243,90,58,.95)",
          boxShadow: "0 0 0 4px rgba(243,90,58,.18), 0 0 34px rgba(243,90,58,.34)",
          animation: "wpTutorialPulse 1.55s ease-in-out infinite",
          "@keyframes wpTutorialPulse": {
            "0%,100%": { boxShadow: "0 0 0 4px rgba(243,90,58,.16), 0 0 26px rgba(243,90,58,.25)" },
            "50%": { boxShadow: "0 0 0 8px rgba(243,90,58,.07), 0 0 38px rgba(243,90,58,.38)" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
    </>
  );
}

function CoachCard({ block, session, onNavigate, onComplete, onDefer, onSkip, onPause, targetFound, rect }) {
  const progress = `${Math.min(session.step + 1, session.planKeys?.length || 1)}/${session.planKeys?.length || 1}`;
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;
  const cardWidth = Math.min(390, Math.max(300, viewportWidth - 28));
  const topPreferred = rect ? rect.bottom + 14 : viewportHeight / 2 - 170;
  const wouldOverflowBottom = topPreferred + 300 > viewportHeight;
  const top = rect && wouldOverflowBottom ? Math.max(14, rect.top - 314) : clamp(topPreferred, 14, Math.max(14, viewportHeight - 320));
  const left = rect
    ? clamp(rect.left, 14, Math.max(14, viewportWidth - cardWidth - 14))
    : Math.max(14, (viewportWidth - cardWidth) / 2);

  return (
    <Paper
      role="dialog"
      aria-label={`Tutorial WebProfit: ${block.label}`}
      elevation={20}
      sx={{
        position: "fixed",
        zIndex: OVERLAY_Z + 4,
        top,
        left,
        width: cardWidth,
        maxHeight: "calc(100dvh - 28px)",
        overflowY: "auto",
        borderRadius: 3.5,
        border: "1px solid rgba(255,255,255,.12)",
        bgcolor: "rgba(25,27,32,.98)",
        color: "#fff",
        boxShadow: "0 30px 90px rgba(0,0,0,.42)",
        p: 2,
      }}
    >
      <Stack spacing={1.35}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <AutoAwesomeRoundedIcon sx={{ color: BRAND.orange, fontSize: 19 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 950, letterSpacing: ".07em", textTransform: "uppercase", color: "rgba(255,255,255,.62)" }}>
              {block.eyebrow}
            </Typography>
          </Stack>
          <Chip size="small" label={progress} sx={{ height: 24, bgcolor: "rgba(255,255,255,.08)", color: "#fff", fontWeight: 900 }} />
        </Stack>

        <Box>
          <Typography sx={{ fontSize: 20, lineHeight: 1.14, fontWeight: 1000 }}>{block.title}</Typography>
          <Typography sx={{ mt: 0.8, fontSize: 13.2, lineHeight: 1.55, color: "rgba(255,255,255,.70)" }}>
            {block.description}
          </Typography>
        </Box>

        {Array.isArray(block.highlights) && block.highlights.length ? (
          <Stack spacing={0.65} sx={{ borderTop: "1px solid rgba(255,255,255,.12)", pt: 1.2 }}>
            {block.highlights.map((point) => (
              <Typography key={point} sx={{ fontSize: 12.2, lineHeight: 1.45, color: "rgba(255,255,255,.85)" }}>
                • {point}
              </Typography>
            ))}
          </Stack>
        ) : null}

        {!targetFound && block.selector ? (
          <Typography sx={{ fontSize: 11.5, lineHeight: 1.45, color: "rgba(255,255,255,.50)" }}>
            Si esta pantalla cambió, no te dejamos atrapado: podés ir a la sección, marcarlo como hecho o pausarlo.
          </Typography>
        ) : null}

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {!targetFound && block.route ? (
            <Button
              variant="contained"
              endIcon={<EastRoundedIcon />}
              onClick={onNavigate}
              sx={{ bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, boxShadow: "none", "&:hover": { bgcolor: "#DE4D31" } }}
            >
              {block.actionLabel || "Ir a la sección"}
            </Button>
          ) : null}
          {block.completion === "manual" || block.completion === "metric_or_manual" ? (
            <Button variant="contained" onClick={onComplete} sx={{ bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, boxShadow: "none", "&:hover": { bgcolor: "#DE4D31" } }}>
              {block.continueLabel || "Listo, seguir"}
            </Button>
          ) : null}
          {block.optional ? (
            <Button variant="text" startIcon={<SkipNextRoundedIcon />} onClick={block.deferPreferred ? onDefer : onSkip} sx={{ color: "rgba(255,255,255,.72)", textTransform: "none", fontWeight: 850 }}>
              {block.deferPreferred ? (block.deferLabel || "Lo veo después") : (block.skipLabel || "Omitir por ahora")}
            </Button>
          ) : null}
          <Button variant="text" startIcon={<PauseRoundedIcon />} onClick={onPause} sx={{ color: "rgba(255,255,255,.58)", textTransform: "none", fontWeight: 800 }}>
            Pausar
          </Button>
        </Stack>

        {targetFound && block.unlockAfterTargetClick ? (
          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "rgba(255,255,255,.58)" }}>
            Tocá la acción resaltada. Después liberamos la pantalla para que completes el formulario normalmente.
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

function EngagedDock({ block, session, onComplete, onSkip, onPause }) {
  const allowManual = block.completion === "metric_or_manual";
  return (
    <Paper
      elevation={14}
      sx={{
        position: "fixed",
        right: { xs: 10, sm: 18 },
        bottom: { xs: 10, sm: 18 },
        zIndex: OVERLAY_Z + 5,
        width: { xs: "calc(100vw - 20px)", sm: 350 },
        borderRadius: 3,
        bgcolor: "rgba(25,27,32,.97)",
        color: "#fff",
        border: "1px solid rgba(243,90,58,.26)",
        boxShadow: "0 18px 60px rgba(0,0,0,.34)",
        p: 1.4,
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box sx={{ width: 9, height: 9, borderRadius: 99, bgcolor: BRAND.orange, boxShadow: "0 0 0 5px rgba(243,90,58,.12)" }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 950 }}>{block.label}</Typography>
          <Typography sx={{ mt: 0.2, fontSize: 11.2, color: "rgba(255,255,255,.62)", lineHeight: 1.35 }}>
            {block.waitingLabel || "Completá esta tarea para continuar."}
          </Typography>
        </Box>
        {allowManual ? (
          <Button size="small" onClick={onComplete} sx={{ minWidth: 0, px: 1, color: BRAND.orange, textTransform: "none", fontWeight: 950 }}>
            Hecho
          </Button>
        ) : null}
        {block.optional ? (
          <IconButton size="small" onClick={onSkip} aria-label="Omitir este paso" sx={{ color: "rgba(255,255,255,.62)" }}>
            <SkipNextRoundedIcon fontSize="small" />
          </IconButton>
        ) : null}
        <IconButton size="small" onClick={onPause} aria-label="Pausar tutorial" sx={{ color: "rgba(255,255,255,.62)" }}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}



// WP-ONBOARDING-ITEM-FOCUS-COACH
// STATUS: IMPLEMENTED / STATIC_PASS / STAGING_PENDING
// CONTRACT: the item tutorial is visual-only guidance. It never mutates business data,
// never makes optional fields mandatory and delegates all real validation to the existing form.
const ITEM_COACH_TOUR = Object.freeze({
  0: [
    { key: "type", selector: '[data-wp-item-coach-target="type"]', title: "Primero: ¿qué estás creando?", body: "Elegí si esto es un producto, servicio u otro tipo de ítem. Esa decisión cambia qué opciones te mostramos después.", example: "Ej.: bicicleta, perfume, consulta profesional o instalación." },
  ],
  1: [
    { key: "name", selector: '[data-wp-item-coach-target="name"]', title: "Este es el nombre que va a reconocer todo el mundo", body: "Poné cómo se llama el producto o servicio. Es el nombre que vas a ver internamente y que puede aparecer en tu web.", example: "Ej.: Bicicleta Mountain Pro 29 · Perfume Example Night · Instalación de aire acondicionado.", autoFocus: true },
    { key: "description", selector: '[data-wp-item-coach-target="description"]', title: "Ahora contá qué es", body: "La descripción funciona como en un e-commerce: explicá para qué sirve, materiales, características, compatibilidades o lo que ayuda a decidir una compra.", example: "Ej.: cuadro de aluminio, rodado 29, frenos hidráulicos; o fragancia amaderada, 100 ml, uso nocturno.", autoFocus: true },
  ],
  2: [
    { key: "classification", selector: '[data-wp-item-coach-target="classification"]', title: "Categoría y subcategoría son una decisión de negocio", body: "Usalas para que tus clientes y tu equipo naveguen mejor el catálogo. Pensá primero el grupo grande y después, sólo si ayuda, una división más específica.", example: "Ej.: Categoría Montaña → Subcategoría Bicicletas. Otro ítem del mismo grupo podría ser Montaña → Botas. También podés usar Bicicletas → Montaña si ésa es la lógica de tu catálogo." },
  ],
  3: [
    { key: "price-type", selector: '[data-wp-item-coach-target="price-type"]', title: "Elegí cómo querés mostrar el precio", body: "Precio fijo sirve cuando el cliente puede pagar ese valor. Consultar sirve cuando primero tiene que preguntarte. Desde sirve cuando el valor final cambia, pero querés mostrar una referencia mínima.", example: "Ej.: $120.000 fijo · Consultar para una instalación a medida · Desde $80.000 cuando depende de la configuración." },
    { key: "price-value", selector: '[data-wp-item-coach-target="price-value"]', title: "Acá va el valor de venta", body: "Este es el precio que verá el cliente según la modalidad elegida. No es el costo interno del negocio.", example: "Si elegiste Consultar, el valor puede quedar sólo como referencia. Si elegiste Desde, cargá el punto de partida.", autoFocus: true },
  ],
  4: [
    { key: "variants", selector: '[data-wp-item-coach-target="variants"]', title: "Variantes: cuando el mismo ítem tiene opciones", body: "Activá variantes si cambia por color, talle, medida, modelo u otra elección. Escribí el nombre de la característica y las opciones separadas por coma; después generá las combinaciones.", example: "Ej.: Color → Verde, Negro. Talle → 39, 40, 41. WebProfit combina Color × Talle automáticamente." },
    { key: "variant-combinations", selector: '[data-wp-item-coach-target="variants"]', title: "Cada combinación puede tener su propio dato", body: "Cuando generes combinaciones vas a poder dejar el precio general o poner un precio propio para una variante. También podés asignarle una imagen específica cuando ya hayas cargado fotos.", example: "Si Negro talle 41 cuesta igual, dejalo heredando el precio general. Si cambia, cargá sólo el precio de esa combinación." },
  ],
  5: [
    { key: "discounts", selector: '[data-wp-item-coach-target="discounts"]', title: "Descuentos por cantidad · opcional", body: "Usalo sólo si vender más unidades cambia el precio. Para la mayoría de los primeros ítems podés dejarlo vacío y seguir.", example: "Ej.: desde 10 unidades, precio mayorista. Si no trabajás por volumen, omitilo." },
  ],
  6: [
    { key: "costs", selector: '[data-wp-item-coach-target="costs"]', title: "Costo interno: sólo para tu gestión", body: "Esto no aparece en la página. Último costo viene de la última compra; costo promedio calcula el histórico; costo de referencia es una estimación editable. El selector define qué costo usa WebProfit como referencia operativa.", example: "Podés arrancar con un costo estimado y después dejar que las compras reales construyan último costo y promedio.", optionalTarget: true },
    { key: "usage", selector: '[data-wp-item-coach-target="usage"]', title: "Definí cómo participa el ítem en tu negocio", body: "Acá decidís si se vende, se compra, controla stock, admite envío, aparece en la web o puede registrarse como gasto. Son comportamientos del ítem, no cantidades.", example: "Una bicicleta suele venderse, comprarse y controlar stock. Un servicio normalmente se vende sin stock físico." },
  ],
  7: [
    { key: "billing", selector: '[data-wp-item-coach-target="billing"]', title: "Facturación puede configurarse después", body: "Si ya tenés facturación conectada, acá definís impuestos y cuentas asociadas al ítem. Si todavía estás aprendiendo a cargar productos, podés omitirlo y volver más adelante.", example: "El ítem se puede crear aunque todavía no tengas la parte fiscal terminada." },
  ],
  8: [
    { key: "tags", selector: '[data-wp-item-coach-target="tags"]', title: "Etiquetas · opcional", body: "Sirven para sumar palabras internas o comerciales que te ayuden a clasificar y encontrar el ítem.", example: "Ej.: premium, exterior, verano, instalación." },
    { key: "technical", selector: '[data-wp-item-coach-target="technical"]', title: "Código, unidad y datos técnicos", body: "El código es tu identificador interno o SKU y puede servir con lectores de código de barras. La unidad indica cómo se vende o mide el ítem.", example: "Ej.: bolsa de alimento por unidad; alimento suelto por gramo o kilo; cable por metro." },
    { key: "additional", selector: '[data-wp-item-coach-target="additional"]', title: "Más datos, sólo si los necesitás", body: "Podés agregar características extra sin ensuciar el alta principal. Son opcionales y se pueden completar después.", example: "Ej.: material, origen, compatibilidad, potencia o cualquier dato técnico propio de tu rubro.", optionalTarget: true },
    { key: "active", selector: '[data-wp-item-coach-target="active"]', title: "Activo o desactivado", body: "Activo queda disponible para nuevas operaciones. Desactivado conserva el registro pero evita usarlo como un ítem vigente.", example: "Útil para productos discontinuados o servicios que pausaste temporalmente." },
    { key: "images", selector: '[data-wp-item-coach-target="images"]', title: "Acá cargás las imágenes", body: "Podés cargar hasta 8 imágenes en esta etapa. No es obligatorio hacerlo ahora: podés crear el ítem y volver después.", example: "Si subís fotos de colores o variantes distintas, después volvé a Variantes y asigná la imagen correcta a cada combinación." },
  ],
  9: [
    { key: "review", selector: '[data-wp-item-coach-target="review"]', title: "Revisá lo esencial", body: "Confirmá que nombre, tipo, precio y comportamiento representen bien lo que vendés. Lo opcional se puede mejorar más adelante.", example: "Después de crear el ítem vamos a seguir con stock y operación real; no hace falta resolver todo ahora." },
    { key: "create", selector: '[data-wp-item-coach-nav="next"]', title: "Listo: creá el ítem", body: "Cuando estés conforme, este botón guarda el ítem. El tutorial sólo acompaña: la validación y el guardado siguen siendo los del formulario real.", example: "Si falta un dato obligatorio, WebProfit te lo va a marcar sin perder lo que ya cargaste." },
  ],
});

function itemCoachEntries(step) {
  return ITEM_COACH_TOUR[Math.max(0, Math.min(9, Number(step || 0)))] || ITEM_COACH_TOUR[0];
}

function ItemFormCoachCard({ step = 0, substep = 0, rect, targetFound, onNext, onPrevious, onSkipStep, onVariantImageHelp, onPause }) {
  const entries = itemCoachEntries(step);
  const item = entries[Math.max(0, Math.min(entries.length - 1, substep))] || entries[0];
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;
  const cardWidth = Math.min(460, Math.max(310, viewportWidth - 24));
  const estimatedCardHeight = 350;
  const canPlaceRight = Boolean(rect && rect.right + cardWidth + 22 <= viewportWidth);
  const canPlaceLeft = Boolean(rect && rect.left - cardWidth - 22 >= 0);
  const left = rect
    ? canPlaceRight
      ? rect.right + 14
      : canPlaceLeft
        ? rect.left - cardWidth - 14
        : clamp((viewportWidth - cardWidth) / 2, 12, Math.max(12, viewportWidth - cardWidth - 12))
    : Math.max(12, (viewportWidth - cardWidth) / 2);
  const top = rect
    ? (canPlaceRight || canPlaceLeft)
      ? clamp(rect.top, 12, Math.max(12, viewportHeight - estimatedCardHeight - 12))
      : rect.bottom + estimatedCardHeight + 24 <= viewportHeight
        ? rect.bottom + 12
        : clamp(rect.top - estimatedCardHeight - 12, 12, Math.max(12, viewportHeight - estimatedCardHeight - 12))
    : Math.max(12, viewportHeight / 2 - estimatedCardHeight / 2);

  return (
    <Paper
      role="dialog"
      aria-label="Guía para crear un ítem"
      elevation={24}
      sx={{
        position: "fixed", zIndex: OVERLAY_Z + 8, top, left, width: cardWidth,
        maxHeight: "calc(100dvh - 24px)", overflowY: "auto", borderRadius: 3.8,
        bgcolor: "rgba(18,20,24,.985)", color: "#fff", border: "1px solid rgba(243,90,58,.34)",
        boxShadow: "0 32px 100px rgba(0,0,0,.58)", p: { xs: 1.7, sm: 2.1 }, pointerEvents: "auto",
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={0.8}>
            <AutoAwesomeRoundedIcon sx={{ color: BRAND.orange, fontSize: 20 }} />
            <Typography sx={{ fontSize: 10.8, fontWeight: 1000, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.62)" }}>
              Crear ítem · Paso {step + 1}/10 · {substep + 1}/{entries.length}
            </Typography>
          </Stack>
          <Button size="small" startIcon={<PauseRoundedIcon />} onClick={onPause} sx={{ minWidth: 0, color: "rgba(255,255,255,.54)", textTransform: "none", fontWeight: 800 }}>Pausar</Button>
        </Stack>
        <Box>
          <Typography sx={{ fontSize: { xs: 21, sm: 24 }, fontWeight: 1000, lineHeight: 1.12 }}>{item.title}</Typography>
          <Typography sx={{ mt: 0.75, fontSize: 13.5, lineHeight: 1.58, color: "rgba(255,255,255,.75)" }}>{item.body}</Typography>
        </Box>
        <Box sx={{ px: 1.2, py: 1, borderRadius: 2.3, bgcolor: "rgba(243,90,58,.10)", border: "1px solid rgba(243,90,58,.20)" }}>
          <Typography sx={{ fontSize: 12.2, lineHeight: 1.48, color: "rgba(255,255,255,.86)" }}>{item.example}</Typography>
        </Box>
        {!targetFound ? <Typography sx={{ fontSize: 11.2, color: "rgba(255,255,255,.48)" }}>Este campo no está disponible con la configuración actual; podés seguir sin bloquear el alta.</Typography> : null}
        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
          <Button variant="contained" endIcon={<EastRoundedIcon />} onClick={onNext} sx={{ bgcolor: BRAND.orange, color: "#17181C", textTransform: "none", fontWeight: 1000, boxShadow: "none", "&:hover": { bgcolor: "#FF714F" } }}>
            {step === 9 && substep === entries.length - 1 ? "Crear ítem" : substep < entries.length - 1 ? "Siguiente" : "Seguir al próximo paso"}
          </Button>
          {substep > 0 ? <Button variant="text" onClick={onPrevious} sx={{ color: "rgba(255,255,255,.68)", textTransform: "none", fontWeight: 850 }}>Anterior</Button> : null}
          {item.key === "images" && onVariantImageHelp ? <Button variant="outlined" onClick={onVariantImageHelp} sx={{ color: "#fff", borderColor: "rgba(255,255,255,.28)", textTransform: "none", fontWeight: 900 }}>Ver cómo asignar fotos a variantes</Button> : null}
          {[2,4,5,7,8].includes(step) ? <Button variant="text" onClick={onSkipStep} sx={{ color: "rgba(255,255,255,.58)", textTransform: "none", fontWeight: 850 }}>Omitir sección</Button> : null}
        </Stack>
        <Typography sx={{ fontSize: 10.7, lineHeight: 1.4, color: "rgba(255,255,255,.40)" }}>
          El foco explica el formulario real. No guardamos lo que escribís y ninguna opción explicativa cambia tus datos por sí sola.
        </Typography>
      </Stack>
    </Paper>
  );
}

function FirstRunBusinessCard({ block, session, project, onSaved, onContinueWithoutSave, onPause }) {
  const existingGoals = React.useMemo(() => projectBusinessGoals(project), [project]);
  const [profile, setProfile] = React.useState(() => normalizeProjectBusinessProfile(project?.businessProfile));
  const [goals, setGoals] = React.useState(() => existingGoals);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    setProfile(normalizeProjectBusinessProfile(project?.businessProfile));
    setGoals(projectBusinessGoals(project));
    setError("");
  }, [project]);

  const suggestedGoals = React.useMemo(() => {
    const byProfile = businessGoalsForProfile(profile);
    const selected = PROJECT_BUSINESS_GOALS.filter((goal) => goals.includes(goal.key));
    const fallback = PROJECT_BUSINESS_GOALS.filter((goal) => ["sellProducts", "offerServices", "manageCustomers", "controlBusiness", "takeReservations"].includes(goal.key));
    const merged = [...byProfile, ...selected, ...fallback];
    const seen = new Set();
    return merged.filter((goal) => {
      if (!goal || seen.has(goal.key)) return false;
      seen.add(goal.key);
      return true;
    }).slice(0, 6);
  }, [profile, goals]);

  const selectProfile = (value) => {
    const nextProfile = normalizeProjectBusinessProfile(value);
    setProfile(nextProfile);
    reportDashboardTutorialMetric(project, "business_profile_selected", { businessProfile: nextProfile || "unknown" });
  };

  const toggleGoal = (goalKey) => {
    setGoals((current) => {
      const selected = current.includes(goalKey);
      const next = selected ? current.filter((key) => key !== goalKey) : [...current, goalKey];
      reportDashboardTutorialMetric(project, "business_goal_toggled", {
        businessGoal: goalKey,
        selected: !selected,
        goalCount: next.length,
      });
      return next;
    });
  };

  const canSave = Boolean(profile) && goals.length > 0 && !saving;
  const progress = `${Math.min(session.step + 1, session.planKeys?.length || 1)}/${session.planKeys?.length || 1}`;

  const save = async () => {
    if (!canSave) return;
    const key = projectKey(project);
    if (!key) return;
    try {
      setSaving(true);
      setError("");
      const response = await projectsApi.updateBusinessConfiguration(key, {
        businessProfile: profile,
        businessGoals: goals,
      });
      const apiProject = response?.project || {};
      const nextProject = {
        ...project,
        ...apiProject,
        businessProfile: normalizeProjectBusinessProfile(apiProject?.businessProfile || profile),
        onboarding: {
          ...(project?.onboarding || {}),
          ...(apiProject?.onboarding || {}),
          businessGoals: Array.isArray(apiProject?.onboarding?.businessGoals)
            ? apiProject.onboarding.businessGoals
            : goals,
        },
      };
      reportDashboardTutorialMetric(nextProject, "business_context_saved", {
        businessProfile: profile,
        businessGoals: goals.slice(0, 8),
        goalCount: goals.length,
        firstRunInline: true,
      });
      onSaved(nextProject);
    } catch (saveError) {
      reportDashboardTutorialMetric(project, "business_context_save_failed", {
        reasonCode: Number(saveError?.status || saveError?.statusCode || 0) ? "api_rejected" : "request_failed",
      });
      setError(saveError?.message || "No pudimos guardar estas preferencias. Podés reintentar o seguir sin quedar atrapado.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      role="dialog"
      aria-label="Primer paso de WebProfit"
      elevation={24}
      sx={{
        position: "fixed",
        zIndex: OVERLAY_Z + 4,
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "calc(100vw - 24px)", sm: 560 },
        maxHeight: "calc(100dvh - 24px)",
        overflowY: "auto",
        borderRadius: 4,
        bgcolor: "rgba(25,27,32,.99)",
        color: "#fff",
        border: "1px solid rgba(255,255,255,.12)",
        boxShadow: "0 34px 110px rgba(0,0,0,.48)",
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <Box component="form" data-wp-telemetry-form="tutorial-business-context" onSubmit={(event) => { event.preventDefault(); save(); }}>
        <Stack spacing={1.8}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <AutoAwesomeRoundedIcon sx={{ color: BRAND.orange, fontSize: 20 }} />
              <Typography sx={{ fontSize: 11, fontWeight: 950, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.60)" }}>
                {block.eyebrow}
              </Typography>
            </Stack>
            <Chip size="small" label={progress} sx={{ height: 24, bgcolor: "rgba(255,255,255,.08)", color: "#fff", fontWeight: 900 }} />
          </Stack>

          <Box>
            <Typography sx={{ fontSize: { xs: 22, sm: 25 }, lineHeight: 1.08, fontWeight: 1000 }}>
              {project?.name ? `Preparemos ${project.name}` : block.title}
            </Typography>
            <Typography sx={{ mt: 0.65, fontSize: 13.2, lineHeight: 1.55, color: "rgba(255,255,255,.68)" }}>
              Sólo necesitamos esto para ordenar las primeras misiones. Después podés cambiarlo cuando quieras.
            </Typography>
          </Box>

          <TextField
            select
            fullWidth
            required
            label="¿Qué tipo de negocio es?"
            value={profile}
            onChange={(event) => selectProfile(event.target.value)}
            inputProps={{ "data-wp-field-key": "businessProfile" }}
            sx={{
              "& .MuiInputBase-root": { bgcolor: "rgba(255,255,255,.06)", color: "#fff" },
              "& .MuiInputLabel-root": { color: "rgba(255,255,255,.60)" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,.15)" },
              "& .MuiSvgIcon-root": { color: "rgba(255,255,255,.60)" },
            }}
          >
            <MenuItem value="" disabled>Elegí una opción</MenuItem>
            {PROJECT_BUSINESS_PROFILES.map((option) => (
              <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 950, color: "rgba(255,255,255,.88)" }}>
              ¿Qué querés ordenar primero?
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: 11.5, color: "rgba(255,255,255,.48)" }}>
              Podés marcar más de una. Esto prioriza el recorrido, no te quita herramientas.
            </Typography>
            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              {suggestedGoals.map((goal) => {
                const selected = goals.includes(goal.key);
                return (
                  <Chip
                    key={goal.key}
                    label={goal.label}
                    clickable
                    onClick={() => toggleGoal(goal.key)}
                    variant={selected ? "filled" : "outlined"}
                    sx={{
                      fontWeight: 850,
                      bgcolor: selected ? "rgba(243,90,58,.18)" : "transparent",
                      color: selected ? "#fff" : "rgba(255,255,255,.72)",
                      borderColor: selected ? "rgba(243,90,58,.68)" : "rgba(255,255,255,.18)",
                      "&:hover": { bgcolor: selected ? "rgba(243,90,58,.24)" : "rgba(255,255,255,.06)" },
                    }}
                  />
                );
              })}
            </Stack>
          </Box>

          {error ? (
            <Typography sx={{ fontSize: 12, lineHeight: 1.45, color: "#FFB4A5" }}>{error}</Typography>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={0.9} alignItems={{ sm: "center" }}>
            <Button
              type="submit"
              variant="contained"
              disabled={!canSave}
              sx={{ bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, boxShadow: "none", "&:hover": { bgcolor: "#DE4D31" } }}
            >
              {saving ? "Guardando…" : "Preparar mi recorrido"}
            </Button>
            {error ? (
              <Button type="button" onClick={onContinueWithoutSave} sx={{ color: "rgba(255,255,255,.66)", textTransform: "none", fontWeight: 800 }}>
                Seguir sin cambiarlo
              </Button>
            ) : null}
            <Button type="button" startIcon={<PauseRoundedIcon />} onClick={onPause} sx={{ ml: { sm: "auto" }, color: "rgba(255,255,255,.50)", textTransform: "none", fontWeight: 800 }}>
              Pausar
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}

function RoadmapIntroCard({ block, session, onComplete, onPause }) {
  const upcoming = (session?.planKeys || [])
    .slice((session?.step || 0) + 1)
    .map((key) => tutorialBlockByKey(key))
    .filter(Boolean)
    .slice(0, 5);
  const progress = `${Math.min(session.step + 1, session.planKeys?.length || 1)}/${session.planKeys?.length || 1}`;

  return (
    <Paper
      role="dialog"
      aria-label="Tu puesta a punto en WebProfit"
      elevation={24}
      sx={{
        position: "fixed",
        zIndex: OVERLAY_Z + 4,
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "calc(100vw - 24px)", sm: 600 },
        maxHeight: "calc(100dvh - 24px)",
        overflowY: "auto",
        borderRadius: 4,
        bgcolor: "rgba(25,27,32,.99)",
        color: "#fff",
        border: "1px solid rgba(255,255,255,.12)",
        boxShadow: "0 34px 110px rgba(0,0,0,.48)",
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <Stack spacing={1.75}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <AutoAwesomeRoundedIcon sx={{ color: BRAND.orange, fontSize: 20 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 950, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.60)" }}>
              {block.eyebrow}
            </Typography>
          </Stack>
          <Chip size="small" label={progress} sx={{ height: 24, bgcolor: "rgba(255,255,255,.08)", color: "#fff", fontWeight: 900 }} />
        </Stack>

        <Box>
          <Typography sx={{ fontSize: { xs: 22, sm: 25 }, lineHeight: 1.08, fontWeight: 1000 }}>{block.title}</Typography>
          <Typography sx={{ mt: 0.65, fontSize: 13.2, lineHeight: 1.55, color: "rgba(255,255,255,.68)" }}>
            No te vamos a tirar dentro del panel sin contexto. Cada misión abre la sección real, te muestra qué hacer y espera una acción concreta.
          </Typography>
        </Box>

        <Stack spacing={0.7}>
          {upcoming.map((item, index) => (
            <Stack
              key={item.key}
              direction="row"
              spacing={1.1}
              alignItems="center"
              sx={{ p: 1.05, borderRadius: 2.5, bgcolor: index === 0 ? "rgba(243,90,58,.10)" : "rgba(255,255,255,.045)", border: `1px solid ${index === 0 ? "rgba(243,90,58,.28)" : "rgba(255,255,255,.07)"}` }}
            >
              <Box sx={{ width: 27, height: 27, borderRadius: 99, display: "grid", placeItems: "center", flexShrink: 0, bgcolor: index === 0 ? BRAND.orange : "rgba(255,255,255,.08)", fontSize: 11, fontWeight: 1000 }}>
                {index + 1}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12.8, fontWeight: 950 }}>{item.label}</Typography>
                <Typography noWrap sx={{ mt: 0.1, fontSize: 11.1, color: "rgba(255,255,255,.50)" }}>{item.title}</Typography>
              </Box>
              {item.optional ? <Chip size="small" label="opcional" sx={{ ml: "auto", height: 21, color: "rgba(255,255,255,.52)", bgcolor: "rgba(255,255,255,.05)", fontSize: 10 }} /> : null}
            </Stack>
          ))}
        </Stack>

        <Typography sx={{ fontSize: 11.7, lineHeight: 1.5, color: "rgba(255,255,255,.48)" }}>
          Si una tarea no aplica, la podés omitir. Si la dejás a medias, WebProfit recuerda dónde estabas.
        </Typography>

        <Stack direction="row" spacing={0.9} alignItems="center">
          <Button
            variant="contained"
            endIcon={<EastRoundedIcon />}
            onClick={onComplete}
            sx={{ bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, boxShadow: "none", "&:hover": { bgcolor: "#DE4D31" } }}
          >
            Empezar
          </Button>
          <Button startIcon={<PauseRoundedIcon />} onClick={onPause} sx={{ ml: "auto", color: "rgba(255,255,255,.50)", textTransform: "none", fontWeight: 800 }}>
            Pausar
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function DashboardTutorialEngine({ project: projectOverride = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { applyProjectUpdate } = useProjects();
  const project = projectOverride;
  const key = projectKey(project);
  // A saved active session must not flash a fullscreen overlay before the
  // first effect gets the chance to auto-pause it on a fresh dashboard entry.
  const [session, setSession] = React.useState(null);
  const [target, setTarget] = React.useState(null);
  const [rect, setRect] = React.useState(null);
  const [targetTimedOut, setTargetTimedOut] = React.useState(false);
  const [itemCoachStep, setItemCoachStep] = React.useState(0);
  const [itemCoachSubstep, setItemCoachSubstep] = React.useState(0);
  const [itemCoachVisible, setItemCoachVisible] = React.useState(false);
  const [itemCoachTarget, setItemCoachTarget] = React.useState(null);
  const [itemCoachRect, setItemCoachRect] = React.useState(null);
  const [itemCoachFloatingOpen, setItemCoachFloatingOpen] = React.useState(false);
  const [itemCoachReturnStep, setItemCoachReturnStep] = React.useState(null);
  const missingReportedRef = React.useRef("");

  React.useEffect(() => {
    const stored = loadDashboardTutorialOnEntry(project);
    setSession(stored);
    setTarget(null);
    setRect(null);
    setTargetTimedOut(false);
    missingReportedRef.current = "";
  }, [key]);

  // Guided onboarding is opt-in from "Ayuda y tutoriales". Even if an older
  // project has onboarding.guidedStart=true, login no longer opens a modal.

  const blockKey = session?.planKeys?.[session?.step] || session?.blockKey || session?.stageKey || "";
  const block = tutorialBlockByKey(blockKey);
  const desiredRoute = block ? dashboardTutorialBlockRoute(block, project) : "";
  const routeMatches = block && desiredRoute
    ? normalizedPath(location.pathname) === normalizedPath(desiredRoute)
    : true;
  const targetEngaged = Boolean(session?.active && blockKey && session?.engagedBlockKey === blockKey);
  // La pantalla sólo queda liberada mientras el usuario sigue dentro de la
  // sección correcta. Si intenta navegar a otra parte antes de completar la
  // misión, el coach vuelve a bloquear y ofrece regresar, evitando bypasses.
  const engaged = targetEngaged && routeMatches;

  React.useEffect(() => {
    const onTutorialEvent = (event) => {
      const incoming = event?.detail?.session;
      if (incoming?.projectKey && key && String(incoming.projectKey) !== key) return;
      if (incoming) setSession(incoming);

      if (event?.detail?.type === "metric") {
        const current = incoming || loadDashboardTutorialState(project);
        if (!current?.active) return;
        const currentBlockKey = current?.planKeys?.[current.step] || current.blockKey || current.stageKey || "";
        const currentBlock = tutorialBlockByKey(currentBlockKey);
        const metricType = String(event?.detail?.metricType || "");
        if (currentBlock?.completeOnMetric?.includes(metricType)) {
          const expectedSessionId = current.sessionId;
          const expectedBlockKey = currentBlockKey;
          window.setTimeout(() => {
            // Race guard: a fast manual confirmation or another metric may have
            // advanced the roadmap during the small success delay. Never allow
            // a stale metric to complete the following block by accident.
            const latest = loadDashboardTutorialState(project);
            const latestBlockKey = latest?.planKeys?.[latest?.step] || latest?.blockKey || latest?.stageKey || "";
            if (!latest?.active || latest?.sessionId !== expectedSessionId || latestBlockKey !== expectedBlockKey) return;
            const next = advanceDashboardTutorial(project, "completed", { metricType });
            setSession(next);
          }, 450);
        }
      }
    };
    window.addEventListener(DASHBOARD_TUTORIAL_EVENT, onTutorialEvent);
    return () => window.removeEventListener(DASHBOARD_TUTORIAL_EVENT, onTutorialEvent);
  }, [key, project]);

  React.useEffect(() => {
    if (!session?.active || !block || engaged || !routeMatches || !block.selector) {
      setTarget(null);
      setRect(null);
      setTargetTimedOut(false);
      return undefined;
    }
    const startedAt = Date.now();
    setTargetTimedOut(false);
    const locate = () => {
      const found = visibleElement(block.selector);
      if (found) {
        setTargetTimedOut(false);
        setTarget(found);
        const nextRect = found.getBoundingClientRect();
        setRect(nextRect);
        if (nextRect.top < 80 || nextRect.bottom > window.innerHeight - 80) {
          found.scrollIntoView({ block: "center", behavior: "smooth" });
        }
        return true;
      }
      if (Date.now() - startedAt >= TARGET_TIMEOUT_MS) {
        setTargetTimedOut(true);
        const reportKey = `${session.sessionId}:${block.key}:${location.pathname}`;
        if (missingReportedRef.current !== reportKey) {
          missingReportedRef.current = reportKey;
          markDashboardTutorialBlocked(project, "target_missing", {
            blockKey: block.key,
            reasonCode: "target_missing",
          });
        }
      }
      return false;
    };
    locate();
    const timer = window.setInterval(locate, TARGET_POLL_MS);
    return () => window.clearInterval(timer);
  }, [block, engaged, location.pathname, project, routeMatches, session?.active, session?.sessionId]);

  React.useEffect(() => {
    if (!target || engaged) return undefined;
    const update = () => {
      if (!target.isConnected) {
        setTarget(null);
        setRect(null);
        return;
      }
      setRect(target.getBoundingClientRect());
    };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    const timer = window.setInterval(update, 350);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      window.clearInterval(timer);
    };
  }, [engaged, target]);

  React.useEffect(() => {
    if (!session?.active || !block?.unlockAfterTargetClick || engaged || !target) return undefined;
    const onPointer = (event) => {
      if (!target.contains(event.target)) return;
      const blockStartedAt = Date.parse(session?.blockStartedAt || session?.stepChangedAt || session?.startedAt || "");
      const targetWaitMs = Number.isFinite(blockStartedAt) ? Math.max(0, Date.now() - blockStartedAt) : 0;
      const next = engageDashboardTutorialTarget(project, block.key, { reason: "target_click", targetWaitMs });
      setSession(next);
    };
    document.addEventListener("click", onPointer, true);
    return () => document.removeEventListener("click", onPointer, true);
  }, [block, engaged, project, session?.active, target]);

  React.useEffect(() => {
    if (!session?.active || !engaged || block?.coachMode !== "item_form") {
      setItemCoachVisible(false);
      setItemCoachTarget(null);
      setItemCoachRect(null);
      setItemCoachFloatingOpen(false);
      return undefined;
    }
    let previousStep = null;
    const inspect = () => {
      const wizard = document.querySelector('[data-wp-item-wizard="true"]');
      if (!wizard) {
        setItemCoachVisible(false);
        return;
      }
      setItemCoachVisible(true);
      const rawStep = Number(wizard.getAttribute("data-wp-item-step") || 0);
      const nextStep = Number.isFinite(rawStep) ? Math.max(0, Math.min(9, rawStep)) : 0;
      if (previousStep !== nextStep) {
        previousStep = nextStep;
        setItemCoachStep(nextStep);
        setItemCoachSubstep(0);
      }
    };
    inspect();
    const timer = window.setInterval(inspect, 140);
    return () => window.clearInterval(timer);
  }, [block?.coachMode, engaged, session?.active]);

  React.useEffect(() => {
    if (!itemCoachVisible || block?.coachMode !== "item_form") {
      setItemCoachTarget(null);
      setItemCoachRect(null);
      return undefined;
    }
    const entries = itemCoachEntries(itemCoachStep);
    const entry = entries[Math.max(0, Math.min(entries.length - 1, itemCoachSubstep))] || entries[0];
    let focusedKey = "";
    let missingTicks = 0;
    const locate = () => {
      // MUI Select/Autocomplete render their options in a portal. While that portal is open
      // we temporarily remove the blackout so the user can actually choose an option.
      setItemCoachFloatingOpen(Boolean(visibleElement('[role="listbox"], [role="menu"]')));
      const found = visibleElement(entry?.selector);
      setItemCoachTarget(found);
      if (!found) {
        setItemCoachRect(null);
        missingTicks += 1;
        if (entry?.optionalTarget && missingTicks >= 5) {
          setItemCoachSubstep((current) => Math.min(itemCoachEntries(itemCoachStep).length - 1, current + 1));
        }
        return;
      }
      missingTicks = 0;
      const nextRect = found.getBoundingClientRect();
      setItemCoachRect(nextRect);
      if (nextRect.top < 86 || nextRect.bottom > window.innerHeight - 86) found.scrollIntoView({ block: "center", behavior: "smooth" });
      if (entry?.autoFocus && focusedKey !== entry.key) {
        focusedKey = entry.key;
        window.setTimeout(() => {
          const focusable = found.matches?.("input,textarea") ? found : found.querySelector?.("input,textarea");
          focusable?.focus?.({ preventScroll: true });
        }, 260);
      }
    };
    locate();
    const timer = window.setInterval(locate, 180);
    return () => window.clearInterval(timer);
  }, [block?.coachMode, itemCoachStep, itemCoachSubstep, itemCoachVisible]);

  React.useEffect(() => {
    if (!session?.active || engaged) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [engaged, session?.active]);

  if (!project || !session?.active || !block) return null;

  const goToBlock = () => {
    if (!desiredRoute) return;
    navigate(desiredRoute);
  };
  const complete = () => setSession(advanceDashboardTutorial(project, "completed", { reasonCode: "manual_confirm" }));
  const skip = () => setSession(advanceDashboardTutorial(project, "skipped", { reasonCode: "optional_skip" }));
  const defer = () => setSession(advanceDashboardTutorial(project, "deferred", { reasonCode: "deferred" }));
  const pause = () => setSession(pauseDashboardTutorial(project, "tutorial_engine_pause"));
  const completeBusinessContext = (nextProject = project, reasonCode = "business_context_confirmed") => {
    const effectiveProject = nextProject || project;
    if (nextProject) applyProjectUpdate(nextProject);
    refreshDashboardTutorialPlan(effectiveProject, "business_context_update");
    setSession(advanceDashboardTutorial(effectiveProject, "completed", { reasonCode }));
  };
  const completeRoadmapIntro = () => {
    reportDashboardTutorialMetric(project, "roadmap_intro_seen", {
      visibleBlockCount: Math.max(0, Number(session?.planKeys?.length || 0) - Number(session?.step || 0) - 1),
    });
    setSession(advanceDashboardTutorial(project, "completed", { reasonCode: "roadmap_intro_confirmed" }));
  };

  if (block.key === "business_context") {
    return (
      <Portal>
        <Box sx={blockerStyle({ inset: 0 })} />
        <FirstRunBusinessCard
          block={block}
          session={session}
          project={project}
          onSaved={(nextProject) => completeBusinessContext(nextProject, "business_context_saved")}
          onContinueWithoutSave={() => completeBusinessContext(project, "business_context_save_bypassed")}
          onPause={pause}
        />
      </Portal>
    );
  }

  if (block.key === "roadmap_intro") {
    return (
      <Portal>
        <Box sx={blockerStyle({ inset: 0 })} />
        <RoadmapIntroCard block={block} session={session} onComplete={completeRoadmapIntro} onPause={pause} />
      </Portal>
    );
  }

  if (engaged) {
    return (
      <Portal>
        {block.coachMode === "item_form" && itemCoachVisible ? (() => {
          const entries = itemCoachEntries(itemCoachStep);
          const currentIndex = Math.max(0, Math.min(entries.length - 1, itemCoachSubstep));
          const nextCoach = () => {
            if (currentIndex < entries.length - 1) {
              setItemCoachSubstep(currentIndex + 1);
              return;
            }
            if (itemCoachReturnStep !== null && itemCoachStep === 4) {
              const returnNav = visibleElement(`[data-wp-item-step-nav="${itemCoachReturnStep}"]`);
              setItemCoachReturnStep(null);
              returnNav?.click?.();
              if (itemCoachReturnStep === 8) window.setTimeout(() => setItemCoachSubstep(4), 220);
              return;
            }
            const nextButton = visibleElement('[data-wp-item-coach-nav="next"]');
            nextButton?.click?.();
          };
          const previousCoach = () => setItemCoachSubstep((value) => Math.max(0, value - 1));
          const skipItemSection = () => {
            const skipButton = visibleElement('[data-wp-item-coach-nav="skip"]');
            if (skipButton) skipButton.click();
            else visibleElement('[data-wp-item-coach-nav="next"]')?.click?.();
          };
          const wizard = document.querySelector('[data-wp-item-wizard="true"]');
          const canShowVariantImageHelp = itemCoachStep === 8
            && Number(wizard?.getAttribute("data-wp-item-image-count") || 0) > 0
            && wizard?.getAttribute("data-wp-item-has-variants") === "true";
          const showVariantImageHelp = () => {
            const variantNav = visibleElement('[data-wp-item-step-nav="4"]');
            if (!variantNav) return;
            setItemCoachReturnStep(8);
            variantNav.click();
            window.setTimeout(() => setItemCoachSubstep(1), 220);
          };
          return (
            <>
              {!itemCoachFloatingOpen ? (itemCoachTarget && itemCoachRect ? <SpotlightBlockers rect={itemCoachRect} /> : <Box sx={blockerStyle({ inset: 0 })} />) : null}
              <ItemFormCoachCard
                step={itemCoachStep}
                substep={currentIndex}
                rect={itemCoachRect}
                targetFound={Boolean(itemCoachTarget && itemCoachRect)}
                onNext={nextCoach}
                onPrevious={previousCoach}
                onSkipStep={skipItemSection}
                onVariantImageHelp={canShowVariantImageHelp ? showVariantImageHelp : null}
                onPause={pause}
              />
            </>
          );
        })() : (
          <EngagedDock block={block} session={session} onComplete={complete} onSkip={skip} onPause={pause} />
        )}
      </Portal>
    );
  }

  const targetFound = Boolean(routeMatches && block.selector && target && rect && !targetTimedOut);
  return (
    <Portal>
      {!targetFound ? <Box sx={blockerStyle({ inset: 0 })} /> : <SpotlightBlockers rect={rect} />}
      <CoachCard
        block={block}
        session={session}
        targetFound={targetFound}
        rect={targetFound ? rect : null}
        onNavigate={goToBlock}
        onComplete={complete}
        onSkip={skip}
        onDefer={defer}
        onPause={pause}
      />
    </Portal>
  );
}
